<div class="lb" (click)="onBackdropClick()">
  <div
    #dialogContainer
    class="lb__panel"
    [class.lb__panel--fullscreen]="isFullscreen"
    [class.lb__panel--immersive]="immersiveMode"
    [ngStyle]="panelStyles"
    (click)="$event.stopPropagation()"
    (touchstart)="onTouchStart($event)"
    (touchend)="onTouchEnd($event)"
    role="dialog"
    aria-modal="true"
    [attr.aria-labelledby]="lightboxTitle ? 'lb-title' : null"
    [attr.aria-label]="lightboxTitle ? null : 'Project image viewer'"
    tabindex="-1">

    <p class="lb__sr-only" aria-live="polite" aria-atomic="true">{{ slideAnnouncement }}</p>

    <!-- Top bar -->
    <div class="lb__bar">
      @if (lightboxTitle) {
      <span class="lb__bar-title" id="lb-title">{{ lightboxTitle }}</span>
      }
      @if (hasMultipleItems) {
      <span class="lb__bar-counter" aria-hidden="true">
        <span class="lb__bar-counter-n">{{ (activeIndex + 1).toString().padStart(2, '0') }}</span>
        <span class="lb__bar-counter-sep">&thinsp;&mdash;&thinsp;</span>
        <span class="lb__bar-counter-total">{{ items.length.toString().padStart(2, '0') }}</span>
      </span>
      }
      <button #closeButton class="lb__close" type="button" (click)="close()" aria-label="Close gallery">
        <i class="fal fa-times" aria-hidden="true"></i>
      </button>
      @if (hasMultipleItems) {
      <button
        class="lb__autoplay"
        type="button"
        (click)="toggleAutoplay()"
        [attr.aria-label]="isAutoplaying ? 'Pause autoplay' : 'Start autoplay'"
        [attr.aria-pressed]="isAutoplaying">
        @if (isAutoplaying) {
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
            <rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>
          </svg>
        } @else {
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
            <polygon points="5 3 19 12 5 21"/>
          </svg>
        }
      </button>
      }
    </div>

    <!-- Stage -->
    <div class="lb__stage-wrap">
      <div
        #stageCanvas
        class="lb__stage-canvas"
        [class.lb__stage-canvas--immersive]="immersiveMode"
        (click)="$event.stopPropagation()">
        @if (lightboxItem) {
          @if (lightboxItem._isText) {
          <article
            class="lb__text"
            [class.lb__text--fade]="fading"
            [class.lb__text--from-prev]="useDirectionalSlide && fading && slideDirection === 'prev'"
            [class.lb__text--from-next]="useDirectionalSlide && fading && slideDirection === 'next'"
            (transitionend)="onMediaTransitionEnd($event)">
            @if (lightboxItem.eyebrow) { <span class="lb__eyebrow">{{ lightboxItem.eyebrow }}</span> }
            @if (lightboxItem.title) { <h2 class="lb__text-title">{{ lightboxItem.title }}</h2> }
            @if (lightboxItem.body) { <p class="lb__text-body">{{ lightboxItem.body }}</p> }
          </article>
          } @else if (lightboxItem._isVideo) {
          <video
            class="lb__media"
            [class.lb__media--fade]="fading"
            [class.lb__media--from-prev]="useDirectionalSlide && fading && slideDirection === 'prev'"
            [class.lb__media--from-next]="useDirectionalSlide && fading && slideDirection === 'next'"
            (transitionend)="onMediaTransitionEnd($event)"
            controls
            playsinline
            [poster]="lightboxItem._posterUrl"
            [muted]="lightboxItem.muted ?? false">
            <source [src]="lightboxItem._mediaUrl" type="video/mp4" />
          </video>
          } @else {
          <div
            class="lb__zoom"
            (wheel)="onImageWheel($event)"
            (touchstart)="onZoomTouchStart($event)"
            (touchmove)="onZoomTouchMove($event)"
            (touchend)="onZoomTouchEnd()"
            (dblclick)="onZoomDoubleClick()">
            @if (imageLoading) {
            <div
              class="lb__loader"
              [class.lb__loader--has-placeholder]="!!lightboxItem._placeholderSrc"
              [style.background-image]="lightboxItem._placeholderSrc ? 'url(' + lightboxItem._placeholderSrc + ')' : null"
              aria-hidden="true">
              @if (!lightboxItem._placeholderSrc) {
              <span class="lb__loader-ring"></span>
              }
            </div>
            }
            <img
              class="lb__media"
              [class.lb__media--fade]="fading"
              [class.lb__media--from-prev]="useDirectionalSlide && fading && slideDirection === 'prev'"
              [class.lb__media--from-next]="useDirectionalSlide && fading && slideDirection === 'next'"
              [class.lb__media--loading]="imageLoading"
              [class.lb__media--zoomed]="imageScale > 1"
              [style.transform]="imageTransform"
              [src]="lightboxItem._lightboxSrc"
              [attr.srcset]="lightboxItem._lightboxSrcSet || null"
              [attr.sizes]="imageSizes"
              loading="eager"
              fetchpriority="high"
              decoding="sync"
              [alt]="lightboxItem.alt || lightboxItem.caption || ('Image ' + (activeIndex + 1))"
              loading="eager"
              decoding="async"
              fetchpriority="high"
              (load)="onImageLoad()"
              (error)="onImageError()"
              (transitionend)="onMediaTransitionEnd($event)" />
          </div>
          }
        }

        @if (hasMultipleItems) {
        <button class="lb__badge lb__badge--prev" type="button" (click)="prev(); $event.stopPropagation()" aria-label="Previous image">
          <i class="fal fa-chevron-left" aria-hidden="true"></i>
        </button>
        <button class="lb__badge lb__badge--next" type="button" (click)="next(); $event.stopPropagation()" aria-label="Next image">
          <i class="fal fa-chevron-right" aria-hidden="true"></i>
        </button>
        }

        @if (showFullscreenControl) {
        <button
          class="lb__badge lb__badge--fs"
          type="button"
          (click)="toggleFullscreen(); $event.stopPropagation()"
          [attr.aria-label]="(isFullscreen || immersiveMode) ? 'Exit fullscreen' : (fullscreenApiSupported ? 'Enter fullscreen' : 'Expand viewer')">
          <i class="fal" [class.fa-compress]="isFullscreen || immersiveMode" [class.fa-expand]="!(isFullscreen || immersiveMode)" aria-hidden="true"></i>
        </button>
        }
      </div>
    </div>

    @if (showThumbnails && hasMultipleItems) {
    <div #thumbsNav class="lb__thumbs" aria-label="Thumbnail navigation">
      @for (item of items; track item._trackKey; let j = $index) {
      <button
        type="button"
        class="lb__thumb"
        [class.lb__thumb--active]="j === activeIndex"
        (click)="openAt(j)"
        [attr.aria-label]="item.title || item.caption || ('Image ' + (j + 1))"
        [attr.aria-current]="j === activeIndex ? 'true' : null">
        @if (item._isVideo) {
        <video muted playsinline preload="none" [poster]="item._posterUrl"></video>
        } @else if (!item._isText) {
        <img [src]="item._thumbSrc" [attr.srcset]="item._thumbSrcSet || null" sizes="4rem" [alt]="''" loading="lazy" decoding="async" />
        } @else {
        <span class="lb__thumb-text">{{ item.eyebrow || item.title || (j + 1) }}</span>
        }
      </button>
      }
    </div>
    }

    <!-- Bottom bar: caption + bullet dots + slide label -->
    <div class="lb__bottom">
      <div class="lb__bottom-caption">
        @if (lightboxItem && lightboxItem._captionMode !== 'hidden') {
          @if (lightboxItem.eyebrow && !lightboxItem.title) {
            <span class="lb__footer-eyebrow">{{ lightboxItem['eyebrow'] }}</span>
          }
          @if (lightboxItem.caption) {
            <p class="lb__footer-caption">{{ lightboxItem['caption'] }}</p>
          }
        }
      </div>
      @if (hasMultipleItems) {
      <nav class="lb__dots" aria-label="Slide navigation">
        @for (item of items; track $index; let j = $index) {
        <button
          type="button"
          class="lb__dot"
          [class.lb__dot--active]="j === activeIndex"
          (click)="openAt(j)"
          [attr.aria-label]="'Go to slide ' + (j + 1) + ' of ' + items.length"
          [attr.aria-current]="j === activeIndex ? 'true' : null">
        </button>
        }
      </nav>
      <span class="lb__slide-label" aria-hidden="true">Slide&thinsp;{{ activeIndex + 1 }}&thinsp;/&thinsp;{{ items.length }}</span>
      }
    </div>

    <!-- Progress line -->
    @if (hasMultipleItems) {
    <div class="lb__progress" aria-hidden="true">
      <div class="lb__progress-bar" [style.width.%]="((activeIndex + 1) / items.length) * 100"></div>
    </div>
    }

  </div>
</div>
